import { config } from "../../config/env";
import { ApiError, ApiErrorType, ErrorCategory, ErrorSeverity } from "../../error/errorTypes";
import {
  EXPENSES_TRACKER_REFRESH_TOKEN_URL,
} from "../../utils/constants/ApiConstants";
import { HttpMethod } from "../../utils/constants/HttpConstants";
import {
  getAccessToken,
  getTokens,
  clearTokens,
  executeRefresh,
  setTokens,
  isAuthenticated,
  isAccessTokenExpiringSoon,
} from "../../auth/tokenUtils";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import type { AuthTokens } from "../../auth/types";

export type QueryParams = Record<string, string | number | boolean | undefined>;
export type HeadersRecord = Record<string, string | undefined>;

export interface RequestOptions {
  query?: QueryParams;
  headers?: HeadersRecord;
  authenticated?: boolean;
  timeoutMs?: number;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: QueryParams): string {
  const base = (config.apiBaseUrl || "").replace(/\/$/, "");
  const relative = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(base + relative, window.location.origin);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined) return;
      url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, controller: AbortController): Promise<T> {
  if (!timeoutMs || timeoutMs <= 0) return promise;
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  return promise.finally(() => clearTimeout(timeout));
}

function normalizeHeaders(headers: HeadersRecord): Record<string, string> {
  const entries = Object.entries(headers).filter(([, v]) => v !== undefined) as Array<[string, string]>;
  return Object.fromEntries(entries);
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function randomId(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function performDirectRefresh(): Promise<AuthTokens> {
  const tokens = getTokens();
  if (!tokens?.refreshToken) {
    throw new Error("No refresh token available");
  }
  const url = buildUrl(EXPENSES_TRACKER_REFRESH_TOKEN_URL);
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ refreshToken: tokens.refreshToken }),
  });

  if (!response.ok) {
    throw new Error(`Refresh failed with status ${response.status}`);
  }
  const data = await safeParseJson(response);
  if (!data?.accessToken || !data?.refreshToken) {
    throw new Error("Invalid token response from server");
  }
  // setTokens is normally invoked by executeRefresh, but we also set here in case direct usage occurs
  setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  return { accessToken: data.accessToken, refreshToken: data.refreshToken };
}

function redirectToLogin(): void {
  clearTokens();
  
  // Don't redirect if already on login page or other auth pages to prevent loops
  const currentPath = window.location.pathname;
  const authPages = [LOGIN_ROUTE_PATH, '/register', '/verify-email', '/forgot-password', '/reset/password'];
  if (authPages.some(page => currentPath.startsWith(page))) {
    console.warn('Already on auth page, skipping redirect');
    return;
  }
  
  try {
    const publicBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const path = `${publicBase}${LOGIN_ROUTE_PATH}`;
    const loginUrl = new URL(path, window.location.origin).toString();
    window.location.href = loginUrl;
  } catch {
    window.location.href = LOGIN_ROUTE_PATH;
  }
}

async function ensureAuthHeader(headers: HeadersRecord = {}): Promise<HeadersRecord> {
  if (!isAuthenticated()) {
    return headers;
  }

  const token = getAccessToken();
  if (token && !isAccessTokenExpiringSoon(token)) {
    return { ...headers, Authorization: `Bearer ${token}` };
  }

  // Try to refresh if expiring soon/expired, using single-flight dedupe
  const refreshed = await executeRefresh(performDirectRefresh);
  if (!refreshed) {
    redirectToLogin();
    throw new ApiError("Authentication expired", ApiErrorType.Unauthorized, {
      category: ErrorCategory.Authentication,
      severity: ErrorSeverity.Medium,
      userMessage: "Your session has expired. Please log in again.",
    });
  }
  const newToken = getAccessToken();
  return newToken ? { ...headers, Authorization: `Bearer ${newToken}` } : headers;
}

async function safeParseJson(response: Response): Promise<any | undefined> {
  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    // No JSON body expected (e.g. 204)
    if (response.status === 204) return undefined;
  }
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function mapToApiError(response: Response, data?: any, originalError?: unknown): ApiError {
  const status = response.status;
  const code: string | undefined = data?.code;
  const title: string | undefined = data?.title;
  const bodyMessage: string | undefined = data?.message;

  let type: ApiErrorType = ApiErrorType.ClientError;
  let category: ErrorCategory = ErrorCategory.Client;
  let severity: ErrorSeverity = ErrorSeverity.Medium;

  if (status === 401) {
    type = ApiErrorType.Unauthorized;
    category = ErrorCategory.Authentication;
  } else if (status === 429) {
    type = ApiErrorType.RateLimitError;
    category = ErrorCategory.Server;
  } else if (status >= 500) {
    type = ApiErrorType.ServerError;
    category = ErrorCategory.Server;
    severity = ErrorSeverity.High;
  } else if (status >= 400) {
    type = ApiErrorType.ClientError;
    category = status === 403 ? ErrorCategory.Authorization : ErrorCategory.Client;
  }

  const message = bodyMessage || title || `Request failed with status ${status}`;
  return new ApiError(message, type, {
    status,
    category,
    severity,
    originalError,
    userMessage: message,
    developerMessage: title && code ? `${title} (${code})` : title || undefined,
    errorCode: code,
  });
}

async function coreRequest<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options: RequestOptions = {}
): Promise<{ data: T; status: number; headers: Headers }> {
  const { query, headers, authenticated = true, timeoutMs, signal } = options;
  const url = buildUrl(path, query);

  const controller = new AbortController();
  if (signal) {
    signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  const requestId = randomId();
  const mergedHeaders: HeadersRecord = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Request-Id": requestId,
    ...headers,
  };

  const finalHeadersMaybe = authenticated ? await ensureAuthHeader(mergedHeaders) : mergedHeaders;
  const finalHeaders = normalizeHeaders(finalHeadersMaybe);

  const requestInit: RequestInit = {
    method: method.toUpperCase(),
    headers: finalHeaders,
    signal: controller.signal,
  };

  if (body !== undefined) {
    requestInit.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  try {
    const fetchOnce = async (): Promise<Response> => {
      const fetchPromise = fetch(url, requestInit);
      return await withTimeout(fetchPromise, timeoutMs ?? config.httpTimeoutMs, controller);
    };
    let response = await fetchOnce();

    if (response.ok) {
      const data = (await safeParseJson(response)) as T;
      return { data: data as T, status: response.status, headers: response.headers };
    }

    const shouldRetry = method === HttpMethod.GET && (response.status === 429 || response.status >= 500);
    if (shouldRetry) {
      const maxRetries = 2;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        const base = 300 * Math.pow(2, attempt - 1);
        const jitter = Math.floor(Math.random() * 100);
        await sleep(base + jitter);
        response = await fetchOnce();
        if (response.ok) {
          const retryOkData = (await safeParseJson(response)) as T;
          return { data: retryOkData as T, status: response.status, headers: response.headers };
        }
        if (!(response.status === 429 || response.status >= 500)) {
          break;
        }
      }
    }

    // 401 handling: distinguish between token expiration and validation errors
    if (response.status === 401 && authenticated) {
      // Parse error to check the specific error code
      const errData = await safeParseJson(response);
      const errorCode = errData?.code;
      
      // Only attempt token refresh for session/token expiration errors
      // All other 401s (like INCORRECT_PASSWORD, AUTHENTICATION_FAILED) should be treated as validation errors
      const isTokenExpired = errorCode === 'SESSION_EXPIRED' || errorCode === 'TOKEN_EXPIRED';
      
      if (!isTokenExpired) {
        // This is a validation error (wrong password, invalid credentials, etc.), not a token issue
        // Don't attempt refresh, just throw the error for the component to handle
        throw mapToApiError(response, errData);
      }
      
      // Token expired - attempt refresh and retry
      const refreshed = await executeRefresh(performDirectRefresh);
      if (refreshed) {
        const retryHeadersMaybe = await ensureAuthHeader(mergedHeaders);
        const retryHeaders = normalizeHeaders(retryHeadersMaybe);
        const retryInit: RequestInit = { ...requestInit, headers: retryHeaders, signal: controller.signal };
        const retryResponse = await withTimeout(fetch(url, retryInit), timeoutMs ?? config.httpTimeoutMs, controller);
        if (retryResponse.ok) {
          const retryData = (await safeParseJson(retryResponse)) as T;
          return { data: retryData as T, status: retryResponse.status, headers: retryResponse.headers };
        }
        // If still unauthorized after refresh, redirect to login
        if (retryResponse.status === 401) {
          redirectToLogin();
        }
        const retryErrData = await safeParseJson(retryResponse);
        throw mapToApiError(retryResponse, retryErrData);
      } else {
        // Refresh failed, redirect to login
        redirectToLogin();
        throw mapToApiError(response, errData);
      }
    }

    // 403 handling: distinguish between authorization failure and validation errors
    if (response.status === 403 && authenticated) {
      const errData = await safeParseJson(response);
      const errorCode = errData?.code;
      
      // Check if this is a validation error (e.g., wrong password, email not verified)
      // These should NOT trigger logout
      const isValidationError = errorCode && (
        errorCode.includes('INCORRECT') ||
        errorCode.includes('WRONG') ||
        errorCode.includes('INVALID') ||
        errorCode.includes('NOT_VERIFIED') ||
        errorCode.includes('PENDING')
      );
      
      if (!isValidationError) {
        // This is an authorization failure (invalid/revoked tokens)
        // User should be logged out immediately
        console.warn('403 Forbidden: Invalid tokens, logging out');
        redirectToLogin();
      }
      
      throw mapToApiError(response, errData);
    }

    // For all other errors, just throw
    const errData = await safeParseJson(response);
    throw mapToApiError(response, errData);
  } catch (error: any) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error?.name === "AbortError") {
      throw new ApiError("Request timed out", ApiErrorType.NetworkError, {
        category: ErrorCategory.Network,
        severity: ErrorSeverity.Medium,
        originalError: error,
        userMessage: "Network timeout. Please try again.",
      });
    }
    throw new ApiError(error?.message || "Network error", ApiErrorType.NetworkError, {
      category: ErrorCategory.Network,
      severity: ErrorSeverity.Medium,
      originalError: error,
      userMessage: "Network error. Please check your connection and try again.",
    });
  }
}

export const httpClient = {
  get: <T>(path: string, options?: RequestOptions) =>
    coreRequest<T>(HttpMethod.GET, path, undefined, options),
  post: <T, B = unknown>(path: string, body: B, options?: RequestOptions) =>
    coreRequest<T>(HttpMethod.POST, path, body, options),
  put: <T, B = unknown>(path: string, body: B, options?: RequestOptions) =>
    coreRequest<T>(HttpMethod.PUT, path, body, options),
  delete: <T = void>(path: string, options?: RequestOptions) =>
    coreRequest<T>(HttpMethod.DELETE, path, undefined, options),
  // Public (unauthenticated) variants
  public: {
    get: <T>(path: string, options?: Omit<RequestOptions, "authenticated">) =>
      coreRequest<T>(HttpMethod.GET, path, undefined, { ...options, authenticated: false }),
    post: <T, B = unknown>(path: string, body: B, options?: Omit<RequestOptions, "authenticated">) =>
      coreRequest<T>(HttpMethod.POST, path, body, { ...options, authenticated: false }),
  },
};


